import { createSelector } from 'reselect';
import isNil from 'lodash/isNil';
import memoize from 'lodash/memoize';
import {
	type StatusCategoryKeys,
	StatusCategoryTypes,
} from '@atlassian/jira-common-constants/src/status-categories';
import type { Column } from '../../../model/columns/types';
import { SPRINT } from '../../../model/constants';
import {
	getIsCompanyManaged,
	getKanbanBacklogColumnId,
	getKanbanBoardColumnId,
	getKanbanTargetColumnId,
	isKanbanBacklogStrategy,
} from '../app';
import { getCardLists } from '../cards';
import { findColumnByStatusId, getColumns, makeGetColumn } from '../columns';

export const getInitialStatusIds = createSelector(
	[getIsCompanyManaged, getColumns],
	(isCompanyManaged, columns) => {
		if (!columns) {
			return [];
		}

		const columnsArray: Column[] = Object.values(columns);
		if (isCompanyManaged) {
			if (columnsArray.length) {
				const initialStatusColumn = columnsArray.find((column) => column.order === 0);
				return initialStatusColumn ? initialStatusColumn.statuses.map((status) => status.id) : [];
			}
		}
		return columnsArray.reduce((initialStatuses: string[], column) => {
			column.statuses.forEach((status) => {
				const isInitialStatus = /* eslint-disable @typescript-eslint/consistent-type-assertions */ (
					[StatusCategoryTypes.NEW, StatusCategoryTypes.UNDEFINED] as StatusCategoryKeys[]
				) /* eslint-enable @typescript-eslint/consistent-type-assertions */
					.includes(status.category);

				isInitialStatus && initialStatuses.push(status.id);
			});
			return initialStatuses;
		}, []);
	},
);

export const getResolutionDoneStatusIds = createSelector(
	[getIsCompanyManaged, getColumns],
	(isCompanyManaged, columns) => {
		if (!columns) {
			return [];
		}

		const columnsArray = Object.values(columns);
		if (isCompanyManaged) {
			if (columnsArray.length) {
				const resolutionDoneStatusColumn = columnsArray.find(
					(column) => column.order === columnsArray.length - 1,
				);
				return resolutionDoneStatusColumn
					? resolutionDoneStatusColumn.statuses.map((status) => status.id)
					: [];
			}
		}
		return columnsArray.reduce((resolutionDoneStatuses: string[], column) => {
			column.statuses.forEach((status) => {
				if (status.category === StatusCategoryTypes.DONE) {
					resolutionDoneStatuses.push(status.id);
				}
			});
			return resolutionDoneStatuses;
		}, []);
	},
);

export const getColumnStatusByStatusId = (statusId: string) =>
	createSelector([getColumns], (columns) => {
		if (!columns) {
			return null;
		}

		// We do this kind of loop because its more efficient than the alternative loops
		for (const columnKey of Object.keys(columns)) {
			const status = (columns[columnKey].statuses || []).find(
				(s) => !isNil(s) && s.id === statusId,
			);

			// Terminate early if we find what we need (flow doesn't like isNil here)
			if (status !== undefined && status !== null) {
				return status;
			}
		}

		return null;
	});

export const getColumnStatusIds = createSelector([getColumns], (columns) => {
	if (!columns) {
		return [];
	}

	const columnsArray = Object.values(columns);
	return columnsArray.reduce((statuseIds: string[], column) => {
		column.statuses.forEach((status) => {
			statuseIds.push(status.id);
		});
		return statuseIds;
	}, []);
});

// Remove on cleanup of isFrontendDrivenBacklogEnabled
export const isStatusMappedToBoardOld = (statusId: string) =>
	createSelector([getColumnStatusIds], (columnStatusIds) => {
		if (!columnStatusIds) {
			return false;
		}
		return columnStatusIds.includes(statusId);
	});

export const isStatusMappedToBoard = createSelector([getColumnStatusIds], (columnStatusIds) =>
	memoize((statusId: string) => {
		if (!columnStatusIds) {
			return false;
		}
		return columnStatusIds.includes(statusId);
	}),
);

export const makeGetKanbanSingleColumnStatusId = createSelector(
	[
		getIsCompanyManaged,
		isKanbanBacklogStrategy,
		getCardLists,
		getKanbanTargetColumnId,
		makeGetColumn,
	],
	(isCompanyManaged, isKanbanStrategy, cardLists, selectKanbanTargetColumnId, getColumn) =>
		(cardListId: string) => {
			if (!isCompanyManaged || !isKanbanStrategy) {
				return undefined;
			}

			const cardList = cardLists[cardListId];
			if (!cardList) {
				return undefined;
			}

			if (cardList.type === SPRINT) {
				return undefined;
			}

			const targetColumnId = selectKanbanTargetColumnId(cardList.type);
			if (!targetColumnId) {
				return undefined;
			}

			const column = getColumn(targetColumnId);
			if (!column) {
				return undefined;
			}

			return column.statuses.length === 1 ? column.statuses[0].id : undefined;
		},
);

/**
 * Returns only the statuses that would make cards visible on a kanban backlog,
 * e.g. statuses that belong to the 'backlog' or 'selectedForDevelopment' columns
 */
export const getStatusIdsVisibleOnCmpKanbanBacklog = createSelector(
	[getColumns, getKanbanBoardColumnId, getKanbanBacklogColumnId],
	(columns, kanbanBoardColumnId, kanbanBacklogColumnId) => {
		if (!columns || !kanbanBoardColumnId || !kanbanBacklogColumnId) {
			return [];
		}

		const boardStatuses = columns[kanbanBoardColumnId].statuses;
		const backlogStatuses = columns[kanbanBacklogColumnId].statuses;

		return [...boardStatuses, ...backlogStatuses].map((columnStatus) => columnStatus.id);
	},
);

export const isStatusVisibleOnCmpKanbanBacklog = createSelector(
	[getStatusIdsVisibleOnCmpKanbanBacklog],
	(visibleStatusIds) => memoize((statusId: string) => visibleStatusIds.includes(statusId)),
);

export const areStatusesFromSameColumn = createSelector([getColumns], (columns) =>
	// The first two statusIds have been named explicitly to ensure
	// at least two statuses are required for comparison
	memoize(
		(firstStatusId: string, secondStatusId: string, ...remainingStatusIds: string[]) => {
			if (!columns) {
				return false;
			}

			// The column that houses the first status is the point of reference.
			// We'll check whether all other statuses exist in this column.
			const columnOfFirstStatus = findColumnByStatusId(columns, firstStatusId);
			const statusesToCheck = [secondStatusId, ...remainingStatusIds];

			if (!columnOfFirstStatus) {
				return false;
			}

			const columnStatusIds = columnOfFirstStatus.statuses.map(({ id }) => id);

			return statusesToCheck.every((statusId) => columnStatusIds.includes(statusId));
		},
		// Construct a custom cache key for memoize as it
		// only uses the first arg for the cache key by default
		(...statusIds) => [...statusIds.concat().sort()].join('_'),
	),
);
